import React, { useCallback, useEffect, useState } from 'react'
import { Button } from '../../lib/components'
import { tradingAdminServices } from '../../services'

export type TransmitterInfo = {
  originatorFullName: string
  originatorStreetAddress: string
  originatorCity: string
  originatorPostalCode: string
  originatorCountry: string
  originatorBankAccountNumber: string
  originatorBankName: string
}

export type Settlement = {
  id: number
  alpacaSettlementId: string | null
  status: string
  systemDate: string
  totalAmountCents: number | null
  interestAmountCents: number | null
  transferCount: number
  reason: string | null
  additionalInfo: string | null
  transmitterInfo: TransmitterInfo
  createdBy: number | null
  createdAt: string
  completedAt: string | null
}

const EMPTY_TRANSMITTER: TransmitterInfo = {
  originatorFullName: '',
  originatorStreetAddress: '',
  originatorCity: '',
  originatorPostalCode: '',
  originatorCountry: '',
  originatorBankAccountNumber: '',
  originatorBankName: '',
}

const FIELDS: Array<{ key: keyof TransmitterInfo; label: string; hint?: string }> = [
  { key: 'originatorFullName', label: 'Sender name' },
  { key: 'originatorBankName', label: 'Sending bank' },
  { key: 'originatorBankAccountNumber', label: 'Sending account number' },
  { key: 'originatorStreetAddress', label: 'Street address' },
  { key: 'originatorCity', label: 'City' },
  { key: 'originatorPostalCode', label: 'Postal code' },
  { key: 'originatorCountry', label: 'Country', hint: '3-letter code, e.g. USA' },
]

const STATUS_LABEL: Record<string, string> = {
  SUBMITTING: 'Submitting',
  PENDING: 'Pending at Alpaca',
  COMPLETED: 'Completed',
  FAILED: 'Failed',
  AWAITING_ADDITIONAL_FUNDS: 'Awaiting more funds',
}

const usd = (cents: number | null) =>
  cents === null
    ? '—'
    : (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' })

type Props = {
  systemDate: string
  settleableCount: number
  settleableCents: number
  deadline: string | null
  onSettled: () => void
}

/**
 * Settles a system date's executed instant funding transfers with Alpaca.
 * The wire is sent from the bank first; this tells Alpaca what it pays off.
 */
const SettlementPanel = ({
  systemDate,
  settleableCount,
  settleableCents,
  deadline,
  onSettled,
}: Props) => {
  const [settlements, setSettlements] = useState<Settlement[]>([])
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState<TransmitterInfo>(EMPTY_TRANSMITTER)
  const [additionalInfo, setAdditionalInfo] = useState('')
  const [wireSent, setWireSent] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  const loadSettlements = useCallback(async () => {
    const res = await tradingAdminServices.listSettlements(systemDate)
    if (res?.status === 200) setSettlements(res.data)
  }, [systemDate])

  useEffect(() => {
    setOpen(false)
    setNotice('')
    setError('')
    void loadSettlements()
  }, [loadSettlements])

  const startSettle = async () => {
    // Pre-fill the sender from the most recent settlement on any date; the
    // account number is masked there, so it is always entered again.
    const res = await tradingAdminServices.listSettlements()
    const last: Settlement | undefined = res?.status === 200 ? res.data[0] : undefined
    setForm(last ? { ...last.transmitterInfo, originatorBankAccountNumber: '' } : EMPTY_TRANSMITTER)
    setAdditionalInfo('')
    setWireSent(false)
    setError('')
    setNotice('')
    setOpen(true)
  }

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setError('')
    const res = await tradingAdminServices.createSettlement({
      systemDate,
      transmitterInfo: { ...form, originatorCountry: form.originatorCountry.toUpperCase() },
      additionalInfo: additionalInfo.trim() || undefined,
    })
    setBusy(false)
    if (res?.status === 201) {
      setOpen(false)
      setNotice(
        `Settlement sent to Alpaca for ${res.data.transferCount} transfer${
          res.data.transferCount === 1 ? '' : 's'
        } (${usd(res.data.totalAmountCents)}). Status: ${
          STATUS_LABEL[res.data.status] ?? res.data.status
        }.`,
      )
      void loadSettlements()
      onSettled()
    } else {
      setError(res?.data?.error?.message || 'Could not create the settlement.')
    }
  }

  const complete = FIELDS.every(({ key }) => form[key].trim().length > 0)

  return (
    <div className='funding-recon__settle'>
      <div className='funding-recon__settle-head'>
        <div>
          <h3>Settle with Alpaca</h3>
          <p>
            {settleableCount > 0
              ? `${settleableCount} executed transfer${
                  settleableCount === 1 ? '' : 's'
                } ready to settle · ${usd(settleableCents)}${
                  deadline ? ` · due by 1 PM ET on ${deadline}` : ''
                }`
              : 'Nothing ready to settle for this date.'}
          </p>
        </div>
        {!open ? (
          <Button
            text={`Settle ${settleableCount} transfer${settleableCount === 1 ? '' : 's'}`}
            variant='primary-btn'
            isDisabled={settleableCount === 0}
            handleClick={() => void startSettle()}
          />
        ) : null}
      </div>

      {notice ? (
        <p role='status' className='funding-recon__notice'>
          {notice}
        </p>
      ) : null}

      {open ? (
        <form className='funding-recon__settle-form' onSubmit={submit}>
          <p className='funding-recon__settle-steps'>
            1. Send one wire of <strong>{usd(settleableCents)}</strong> to the firm&apos;s Alpaca
            settlement account. 2. Enter the details of the account the wire came from. Alpaca
            checks the money has arrived before settling.
          </p>
          <div className='funding-recon__settle-grid'>
            {FIELDS.map(({ key, label, hint }) => (
              <label key={key}>
                <span>{label}</span>
                <input
                  value={form[key]}
                  maxLength={key === 'originatorCountry' ? 3 : 200}
                  placeholder={hint}
                  autoComplete='off'
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                  required
                />
              </label>
            ))}
            <label className='funding-recon__settle-wide'>
              <span>Note for Alpaca (optional)</span>
              <input
                value={additionalInfo}
                maxLength={500}
                placeholder='e.g. bulk wire reference'
                onChange={(e) => setAdditionalInfo(e.target.value)}
              />
            </label>
          </div>
          <label className='funding-recon__check'>
            <input
              type='checkbox'
              checked={wireSent}
              onChange={(e) => setWireSent(e.target.checked)}
            />
            <span>The wire for {usd(settleableCents)} has been sent</span>
          </label>
          {error ? <p className='funding-recon__error'>{error}</p> : null}
          <div className='funding-recon__settle-actions'>
            <Button
              text={busy ? 'Settling…' : 'Confirm settlement'}
              variant='primary-btn'
              buttonType='submit'
              handleClick={() => undefined}
              isDisabled={busy || !wireSent || !complete}
            />
            <Button
              text='Cancel'
              variant='secondary-btn'
              isDisabled={busy}
              handleClick={() => setOpen(false)}
            />
          </div>
        </form>
      ) : null}

      {settlements.length > 0 ? (
        <div className='w-100 table_scroll_horizontal'>
          <table className='styled-table'>
            <thead>
              <tr>
                <th>Settlement</th>
                <th>Status</th>
                <th>Transfers</th>
                <th>Total</th>
                <th>Interest</th>
                <th>Sent from</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {settlements.map((s) => (
                <tr key={s.id}>
                  <td>
                    #{s.id}
                    {s.alpacaSettlementId ? <small>{s.alpacaSettlementId}</small> : null}
                  </td>
                  <td>
                    {STATUS_LABEL[s.status] ?? s.status}
                    {s.reason ? <small>{s.reason}</small> : null}
                  </td>
                  <td>{s.transferCount}</td>
                  <td>{usd(s.totalAmountCents)}</td>
                  <td>{usd(s.interestAmountCents)}</td>
                  <td>
                    {s.transmitterInfo.originatorBankName}
                    <small>{s.transmitterInfo.originatorBankAccountNumber}</small>
                  </td>
                  <td>{new Date(s.createdAt).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  )
}

export { SettlementPanel }
